import { Injectable } from '@angular/core';
import { MenuItem } from 'src/app/core/models/main-menu.models';
import { TranslateService } from '@ngx-translate/core';
import { UserAccessLevel } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class MenuConfigService {
  constructor(private translate: TranslateService) {}

  /**
   * Returns default menu items for the given section.
   * The titles are translated using the TranslateService.
   * @param section - The menu section (e.g., 'analytics', 'admin', 'source-catalog')
   */
  getDefaultMenuItems(section: string): Array<MenuItem> {
    const config: { [key: string]: Array<MenuItem> } = {
      analytics: [
        {
          id: 1,
          title: this.translate.instant('menu.configure'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.CLIENT_USER,
          children: [
            {
              title: this.translate.instant('menu.workspaces'),
              icon: ['fas', 'folder'],
              link: 'analytics/home',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.data_feeds'),
              icon: ['fas', 'rss'],
              link: 'analytics/feeds',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.dashboards'),
              icon: ['fas', 'tachometer-alt'],
              link: 'analytics/dashboards',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.dimensions'),
              icon: ['fas', 'layer-group'],
              link: 'analytics/dimensions',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.alerts'),
              icon: ['fas', 'bell'],
              link: 'analytics/alerts',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.export-schedules'),
              icon: ['fas', 'calendar-alt'],
              link: 'analytics/schedules',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.auto-story-runs'),
              icon: ['fas', 'wand-magic-sparkles'],
              link: 'analytics/auto-story-runs',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
          ],
        },
        {
          id: 2,
          title: this.translate.instant('menu.monitor'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.CLIENT_USER,
          children: [
            {
              title: this.translate.instant('menu.notifications-center'),
              icon: ['fas', 'flag'],
              link: 'analytics/notifications',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.alerts-center'),
              icon: ['fas', 'bell'],
              link: 'analytics/alerts-center',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
          ],
        },
        {
          id: 3,
          title: this.translate.instant('menu.data'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.CLIENT_ADMIN,

          children: [
            {
              title: this.translate.instant('menu.connectors'),
              icon: ['fas', 'plug'],
              link: 'connector-manager',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
          ],
        },
        // {
        //   id: 4,
        //   title: this.translate.instant('menu.settings'),
        //   isHeader: true,
        //   expanded: true,
        //   children: [
        //     {
        //       title: this.translate.instant('menu.account_settings'),
        //       icon: ['fas', 'cog'],
        //       link: 'analytics/settings',
        //     },
        //   ],
        // },
      ],
      admin: [
        {
          id: 6,
          title: this.translate.instant('menu.client_management'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.companies'),
              icon: ['fas', 'building'],
              link: 'admin/companies',
              user_access_level: UserAccessLevel.AGENCY_ADMIN,
            },
            {
              title: this.translate.instant('menu.data_categories'),
              icon: ['fas', 'tags'],
              link: 'admin/categories',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.export-templates'),
              icon: ['fas', 'file-export'],
              link: 'admin/export-templates',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.connectors'),
              icon: ['fas', 'plug'],
              link: 'admin/connectors',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
          ],
        },
        {
          id: 7,
          title: this.translate.instant('menu.user_management'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.users'),
              icon: ['fas', 'user'],
              link: 'admin/home',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.user-sessions'),
              icon: ['fas', 'user-clock'],
              link: 'admin/user-sessions',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.user_groups'),
              icon: ['fas', 'users-cog'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
              link: 'admin/groups',
            },
            {
              title: this.translate.instant('menu.roles'),
              icon: ['fas', 'user-shield'],
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
              link: 'admin/roles',
            },
            {
              title: this.translate.instant('menu.platform-identities'),
              icon: ['fas', 'user-tag'],
              link: ['/admin', 'platform-identities'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
          ],
        },
        {
          id: 10,
          title: this.translate.instant('menu.automations'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.export-schedule'),
              icon: ['fas', 'calendar-alt'],
              link: 'admin/export-schedules',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
          ],
        },
        {
          id: 8,
          title: this.translate.instant('menu.audit-trails'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.activity_logs'),
              icon: ['fas', 'clipboard-list'],
              link: ['/admin', 'activity-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.search_logs'),
              icon: ['fas', 'search'],
              link: ['/admin', 'search-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.output_logs'),
              icon: ['fas', 'file-export'],
              link: ['/admin', 'output-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.synopsis_logs'),
              icon: ['fas', 'book-open'],
              link: ['/admin', 'synopsis-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.developer_api_company_list'),
              icon: ['fas', 'code'],
              link: ['/admin', 'developer-api-company-list'],
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant(
                'menu.developer_api_company_usage_list',
              ),
              icon: ['fas', 'code'],
              link: ['/admin', 'developer-api-company-usage-list'],
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
        {
          id: 10,
          title: this.translate.instant('menu.system_settings'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.PLATFORM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.nomenclatures'),
              icon: ['fas', 'hashtag'],
              link: 'admin/nomenclature-management',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
          ],
        },
      ],
      sysadmin: [
        {
          id: 12,
          title: this.translate.instant('menu.system_management'),
          isHeader: true,
          expanded: true,
          // Header lowered to GLOBAL_ADMIN so level-50 admins reach the Use Case
          // Templates child below; filterMenuByAccessLevel drops a header for any
          // user under its level, taking its children with it, so the header floor
          // must not exceed its lowest child. The SYSTEM_ADMIN children stay hidden
          // from level-50 by their own per-node level (proposal §12 Option A).
          user_access_level: UserAccessLevel.GLOBAL_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.dashboard'),
              icon: ['fas', 'chart-line'],
              link: 'sysadmin/dashboard',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.worker-management'),
              icon: ['fas', 'microchip'],
              link: 'sysadmin/worker-management',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.cron-jobs'),
              icon: ['fas', 'clock'],
              link: 'sysadmin/cron-jobs',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.system-configuration'),
              icon: ['fas', 'sliders'],
              link: 'sysadmin/system-configuration',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.use-case-templates'),
              icon: ['fas', 'wand-magic-sparkles'],
              link: 'sysadmin/use-case-templates',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
          ],
        },
        {
          id: 13,
          title: this.translate.instant('menu.app_management'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.internationalization'),
              icon: ['fas', 'globe'],
              link: 'sysadmin/translations',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.help-manager'),
              icon: ['fas', 'circle-info'],
              link: 'sysadmin/help-manager',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
      ],
      'mobile-admin': [
        {
          id: 14,
          title: this.translate.instant('menu.mobile_app_management'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.PLATFORM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.dashboard'),
              icon: ['fas', 'chart-line'],
              link: 'mobile-admin/dashboard',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
            {
              title: this.translate.instant('menu.mobile_app_settings'),
              icon: ['fas', 'sliders'],
              link: 'mobile-admin/settings',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
            {
              title: this.translate.instant('menu.mobile_app_strings'),
              icon: ['fas', 'language'],
              link: 'mobile-admin/strings',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
          ],
        },
      ],
      'source-catalog': [
        {
          id: 11,
          title: this.translate.instant('menu.source_catalog'),
          isHeader: true,
          expanded: true,
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.import-content-logs'),
              icon: ['fas', 'history'],
              link: 'source-catalog/import-content-logs',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.http-request-log'),
              icon: ['fas', 'exchange-alt'],
              link: 'source-catalog/http-request-log',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.data-provider-harvest-log'),
              icon: ['fas', 'list-check'],
              link: 'source-catalog/data-provider-harvest-log',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.harvest-remediation'),
              icon: ['fas', 'wand-magic-sparkles'],
              link: 'source-catalog/harvest-remediation',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.data-providers'),
              icon: ['fas', 'network-wired'],
              link: 'source-catalog/data-providers',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.url-playground'),
              icon: ['fas', 'flask'],
              link: 'source-catalog/url-playground',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.settings'),
              icon: ['fas', 'gear'],
              link: 'source-catalog/source',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
      ],
    };

    return config[section] || [];
  }
}
